import { addDays, dateOf, monthRange, toInstant } from './time';
import { sum, type Rupees } from './money';
import { calc, span } from './pricing';
import type { BookingRecord, Expense, Investment, World } from './types';

export interface MonthFigures {
  year: number; month0: number;
  /** Revenue from bookings that START in this month (confirmed or completed). */
  ev: Rupees; st: Rupees;
  /** Cancellation retention, counted in the month of cancellation. */
  other: Rupees;
  rev: Rupees; exp: Rupees; net: Rupees;
  /** Cash in minus refunds, by payment date. Caution deposits are excluded. */
  col: Rupees;
  /** One-off capital purchases. Not deducted from net. */
  inv: Rupees;
  count: number; evN: number; stN: number; days: number;
  enq: number; conf: number;
  src: Record<string, [number, number]>;
}

const BOOKED = new Set(['confirmed', 'completed']);

/**
 * Splits a booking's total into event and stay revenue.
 * Items go to their own kind. Shared charges (cleaning, extra guests, discount, damage) go to the
 * booking's kind; on a combined booking they go to the event part (the occasion). The prototype
 * instead put the remainder into stays, which made discounted event-only bookings show negative
 * stay revenue.
 */
export function revenueSplit(r: BookingRecord): { ev: Rupees; st: Rupees } {
  const k = calc(r);
  const evItems = sum(r.items.filter(i => i.kind === 'event').map(i => i.agreed));
  const stItems = sum(r.items.filter(i => i.kind === 'stay').map(i => i.agreed));
  const shared = k.total - evItems - stItems;
  return r.kind === 'stay' ? { ev: evItems, st: stItems + shared } : { ev: evItems + shared, st: stItems };
}

const inRange = (d: string, s: string, e: string) => d >= s && d < e;

export function month(world: World & { expenses: readonly Expense[]; investments: readonly Investment[] }, year: number, month0: number, sources: readonly string[]): MonthFigures {
  const { start, end } = monthRange(year, month0);
  let ev = 0, st = 0, other = 0, count = 0, evN = 0, stN = 0;
  const days = new Set<string>();
  for (const r of world.records) {
    if (r.status === 'cancelled' && r.cancel && inRange(r.cancel.date, start, end)) other += r.cancel.retained;
    if (!BOOKED.has(r.status)) continue;
    const sp = span(r), sd = dateOf(sp.start);
    if (!inRange(sd, start, end)) continue;
    count++;
    const split = revenueSplit(r);
    ev += split.ev; st += split.st;
    if (r.kind === 'stay') stN++; else evN++;
    const lastDay = dateOf(toInstant(sp.end) - 1);
    for (let d = sd; d <= lastDay; d = addDays(d, 1)) if (inRange(d, start, end)) days.add(d);
  }
  const exp = sum(world.expenses.filter(x => !x.voided && inRange(x.date, start, end)).map(x => x.amount));
  const inv = sum(world.investments.filter(x => !x.voided && inRange(x.date, start, end)).map(x => x.amount));
  const col = sum(world.records.flatMap(r => r.payments).filter(p => !p.voided && inRange(p.date, start, end)).map(p => (p.type === 'refund' ? -p.amount : p.amount)));
  const created = world.records.filter(r => inRange(dateOf(r.created), start, end));
  const converted = (r: BookingRecord) => BOOKED.has(r.status) || r.status === 'cancelled';
  const src: Record<string, [number, number]> = {};
  for (const s of sources) src[s] = [0, 0];
  for (const r of created) {
    const e = (src[r.source] ??= [0, 0]);
    e[0]++; if (converted(r)) e[1]++;
  }
  const rev = ev + st + other;
  return { year, month0, ev, st, other, rev, exp, net: rev - exp, col, inv, count, evN, stN, days: days.size, enq: created.length, conf: created.filter(converted).length, src };
}

/** Percentage change, or null when there is no base to compare with. */
export function delta(cur: number, prev: number): number | null {
  if (prev === 0) return null;
  return Math.round(((cur - prev) / Math.abs(prev)) * 1000) / 10;
}
